import { ReactNode } from 'react';

import { Card } from '@splitfiesta/ui';

import {
  Skeleton,
  SkeletonButton,
  SkeletonCard,
  SkeletonText,
  SkeletonTitle,
} from './SkeletonComponents';
import { SummaryContentSkeleton } from './group-tabs/SummaryTabSkeleton';

/**
 * Group Header Skeleton - Just header + tabs (no content)
 * Used by GroupPageLayout to compose with tab-specific content skeletons
 */
export function GroupHeaderSkeleton({ children }: { children?: ReactNode }) {
  return (
    <div className="space-y-8">
      {/* Header - matches GroupHeader structure */}
      <div className="flex flex-col sm:flex sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Back button */}
          <div className="shrink-0 mt-1">
            <Skeleton className="w-10 h-10 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1">
              <SkeletonTitle className="w-48 h-8 sm:h-9" />
              <Skeleton className="w-12 h-6 rounded-full shrink-0" />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <SkeletonText className="w-20 h-4" />
              <Skeleton className="w-1 h-4" />
              <SkeletonText className="w-32 h-4" />
              <Skeleton className="w-1 h-4" />
              <SkeletonText className="w-24 h-4" />
            </div>
            <SkeletonText className="w-64 h-4 sm:h-5 mt-3" />
          </div>
        </div>
        {/* Add Expense Button + Settings link */}
        <div className="shrink-0 w-full sm:w-auto">
          <div className="flex flex-col gap-2 sm:items-end">
            <SkeletonButton className="w-full sm:w-40 h-10" />
            <div className="w-full sm:w-auto flex justify-center sm:justify-end">
              <SkeletonText className="w-20 h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - matches GroupTabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <nav className="-mb-px flex gap-4 sm:gap-6 overflow-x-auto">
          {[
            'Overview',
            'Activity',
            'Balances',
            'Settlements',
            'Reports',
            'Members',
          ].map((_, i) => (
            <div key={i} className="flex items-center gap-2 pb-3 px-1">
              <SkeletonText className="w-20 h-4" />
            </div>
          ))}
        </nav>
      </div>

      {/* Content area - tab-specific skeleton */}
      {children}
    </div>
  );
}

/**
 * Group Detail Skeleton - Full page skeleton for Summary/Overview tab
 * Composes GroupHeaderSkeleton + SummaryContentSkeleton
 * Used as default when loading the group page
 */
export function GroupDetailSkeleton() {
  return (
    <div className="max-w-2xl mx-auto">
      <GroupHeaderSkeleton>
        <SummaryContentSkeleton />
      </GroupHeaderSkeleton>
    </div>
  );
}

/**
 * Holds a group settings row's place (Pro, Repeating expenses) while its
 * query loads, so the edit form below it does not jump when the row arrives.
 */
export function GroupSettingsRowSkeleton({
  'data-testid': dataTestId,
}: {
  'data-testid'?: string;
}) {
  return (
    <Card
      className="mb-6"
      noHoverEffect
      data-testid={dataTestId}
      aria-hidden="true"
    >
      <div className="flex min-h-11 items-center gap-3">
        <Skeleton className="h-5 w-5 rounded-full" />
        <SkeletonText className="w-40" />
        <Skeleton className="ml-auto h-5 w-5" />
      </div>
    </Card>
  );
}

// Group Form Skeleton
export function GroupFormSkeleton() {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8">
        <Skeleton className="w-10 h-10 rounded-lg mr-4" />
        <div>
          <SkeletonTitle className="w-48 h-8 mb-1" />
          <SkeletonText className="w-96" />
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-6">
        {/* Basic Information Card - Matches GroupBasicInfo structure */}
        <SkeletonCard>
          <div className="space-y-6">
            <SkeletonTitle className="w-40 h-6 mb-4" />

            {/* Name field */}
            <div>
              <SkeletonText className="w-24 h-4 mb-2" />
              <Skeleton className="w-full h-10 rounded-lg" />
            </div>

            {/* Category section */}
            <div>
              <SkeletonText className="w-20 h-4 mb-2" />
              <SkeletonText className="w-full h-3 mb-4" />
              {/* Category button and Wrong category link */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Skeleton className="h-12 rounded-lg" />
                <div className="flex items-center sm:justify-center">
                  <SkeletonText className="w-28 h-4" />
                </div>
              </div>
            </div>

            {/* Description and Group Image - Grid layout on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Description field */}
              <div>
                <SkeletonText className="w-32 h-4 mb-2" />
                <Skeleton className="w-full h-[224px] rounded-lg" />
                <SkeletonText className="w-16 h-3 mt-1 ml-auto" />
              </div>

              {/* Group Image section */}
              <div>
                <SkeletonText className="w-24 h-4 mb-1" />
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 h-[224px] flex flex-col items-center justify-center">
                  <Skeleton className="h-16 w-16 rounded-full mb-3" />
                  <SkeletonText className="w-48 h-4 mb-2" />
                  <SkeletonText className="w-40 h-3" />
                </div>
              </div>
            </div>
          </div>
        </SkeletonCard>

        {/* Money Settings Card */}
        <SkeletonCard>
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Skeleton className="w-5 h-5" />
              <SkeletonTitle className="w-32 h-6" />
            </div>

            {/* Currency and Budget fields in grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Currency dropdown */}
              <div>
                <SkeletonText className="w-16 h-4 mb-2" />
                <Skeleton className="w-full h-10 rounded-lg" />
                <SkeletonText className="w-32 h-3 mt-1" />
              </div>

              {/* Budget field */}
              <div>
                <SkeletonText className="w-24 h-4 mb-2" />
                <Skeleton className="w-full h-10 rounded-lg" />
                <SkeletonText className="w-28 h-3 mt-1" />
              </div>
            </div>
          </div>
        </SkeletonCard>

        {/* Simplify Settlements Card */}
        <SkeletonCard>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="w-5 h-5" />
                <SkeletonTitle className="w-40 h-6" />
              </div>
              <SkeletonText className="w-20 h-4" />
            </div>
            <SkeletonText className="w-full h-4" />
            <div className="flex items-center justify-between">
              <SkeletonText className="w-48 h-5" />
              <Skeleton className="w-12 h-6 rounded-full" />
            </div>
          </div>
        </SkeletonCard>

        {/* Payment Reminders Card */}
        <SkeletonCard>
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Skeleton className="w-5 h-5" />
              <SkeletonTitle className="w-40 h-6" />
            </div>
            <div className="flex items-center justify-between">
              <SkeletonText className="w-32 h-5" />
              <Skeleton className="w-12 h-6 rounded-full" />
            </div>
          </div>
        </SkeletonCard>

        {/* Members Card */}
        <SkeletonCard>
          <div className="space-y-6">
            <div>
              <SkeletonTitle className="w-24 h-6 mb-2" />
              <SkeletonText className="w-48" />
            </div>

            {/* Info message */}
            <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <Skeleton className="w-5 h-5 rounded-full" />
              <SkeletonText className="w-80 h-4" />
            </div>
          </div>
        </SkeletonCard>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-6">
          <SkeletonButton className="w-20 h-10" />
          <SkeletonButton className="w-32 h-10" />
        </div>
      </div>
    </div>
  );
}
